/* eslint-disable react-hooks/exhaustive-deps */
import { Fragment, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Grid, MenuItem, Select, Skeleton, TextField, Typography } from "@mui/material";
import { FormikErrors, useFormik } from "formik";
import * as yup from "yup";
import dayjs from "dayjs";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import * as Sentry from "@sentry/react";
import {
  AccordionCard,
  Callout,
  Checkbox,
  ChoiceToggle,
  CountrySelect,
  FieldWrapper,
  PageHeader,
  PepPersonFields,
  PepPersonValue,
  StepActions,
  selectLoadingProps,
} from "@components/ui_v2";
import CustomDynamicQuestionField from "@components/CustomDynamicQuestionField";
import useSolicitudCreditoForm from "@hooks/useSolicitudCreditoForm";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { useLazyGetCuentasByIdQuery, usePatchCuentaMutation } from "@api/account";
import { useLazyGetByCpQuery } from "@api/postalCodes";
import { useLazyGetAddressQuery, usePostAddressMutation, usePatchAddressMutation } from "@api/address";
import {
  useGetBusinessActivitiesQuery,
  useLazyGetAddressTypeQuery,
  useLazyGetCargoPublicoQuery,
  useLazyGetDependeciasQuery,
  useLazyGetPaymentMethodQuery,
  useLazyGetPepsTypeQuery,
  useLazyGetRelationshipQuery,
} from "@api/catalogs";
import { useLazyGetPersonsQuery } from "@api/personasApi";
import {
  useLazyGetPaymentDetailsQuery,
  useLazyGetPaymentMethodByIdQuery,
  usePostPaymentDetailsMutation,
  usePostPaymentMethodMutation,
  usePutApplicationCompletedMutation,
  usePutPaymentDetailsMutation,
} from "@api/applications";
import {
  useDeletePepsMutation,
  useLazyGetPepsByPersonQuery,
  usePatchPepsDataMutation,
  usePotsPepsDataMutation,
} from "@api/pepsApi";
import { useLazyGetDynamicQuestionByApplicationQuery, usePostDynamicQuestionMutation } from "@api/dynamicQuestion";
import {
  Address,
  CargoPublicoData,
  ColoniaStructure,
  DependenciasData,
  PaymentMethod,
  RelationshipData,
} from "@interfaces/catalogs";
import { AddressData, ApiError, DynamicQuestionAnswerInput, PayLoadPeps, PepsData } from "@interfaces/response";
import {
  ADDRESS_CODES,
  ALTO_SKELETON_SOLICITUD_COMPLETA,
  PATHS_EXPEDIENTE_V2,
  PAYMENT_METHOD_CATALOG,
  PEP_TYPE_CATALOG,
  PERSON_CATALOG,
} from "@helpers/constantes";
import {
  buildDynamicAnswersValidationSchema,
  buildDynamicQuestionAnswerItems,
  isDynamicQuestionVisible,
  mapDynamicQuestionAnswerRecordsToFormValues,
} from "@helpers/dynamicQuestionsHelpers";
import { separarNombres } from "@helpers/funciones";
import { PHONE_NUMBER } from "@utils/expreciones";

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

const PATH_EXPEDIENTE = PATHS_EXPEDIENTE_V2.empresa.path;
const MAX_PEPS = 5;

const emptyPep = (): PepPersonValue => ({
  parentesco: "",
  nombre: "",
  ap_paterno: "",
  ap_materno: "",
  dependencia: "",
  cargo: "",
  actualmente: "",
  start_date: "",
  end_date: "",
  id: "",
});

const pepToValue = (item: PepsData): PepPersonValue => {
  const { nombre, apellido1, apellido2 } = separarNombres(item.name.trim());
  return {
    parentesco: item.relationship_id || "",
    nombre,
    ap_paterno: apellido1,
    ap_materno: apellido2,
    dependencia: item.dependency,
    cargo: item.position,
    actualmente: item.active ? "true" : "false",
    start_date: dayjs(item.start_date).format("YYYY-MM-DD"),
    end_date: item.end_date ? dayjs(item.end_date).format("YYYY-MM-DD") : "",
    id: item.id,
  };
};

const pepItemSchema = (contextKey: "socioFuncionario" | "conyuge", requireParentesco: boolean) => {
  const requiredWhenYes = (schema: yup.StringSchema) =>
    schema.when(`$${contextKey}`, ([respuesta], current: yup.StringSchema) =>
      respuesta === "true" ? current.required("Campo requerido") : current.notRequired(),
    );

  return yup.object().shape({
    parentesco: requireParentesco ? requiredWhenYes(yup.string()) : yup.string().notRequired(),
    nombre: requiredWhenYes(yup.string()),
    ap_paterno: requiredWhenYes(yup.string()),
    ap_materno: requiredWhenYes(yup.string()),
    dependencia: requiredWhenYes(yup.string()),
    cargo: requiredWhenYes(yup.string()),
    actualmente: requiredWhenYes(yup.string()),
    start_date: requiredWhenYes(
      yup
        .string()
        .matches(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha no válido")
        .test("start_date_not_in_future", "La fecha de inicio no puede ser mayor al día actual", (value) =>
          value ? dayjs(value).isSameOrBefore(dayjs(), "day") : true,
        ),
    ),
    end_date: yup
      .string()
      .matches(/^\d{4}-\d{2}-\d{2}$/, "Formato de fecha no válido")
      .when("actualmente", {
        is: "false",
        then: (schema) =>
          schema
            .required("Campo requerido")
            .test(
              "end_date_greater_than_start_date",
              "La fecha de término no puede ser menor que la fecha de inicio",
              function (value) {
                const { start_date } = this.parent;
                return start_date && value ? dayjs(value).isSameOrAfter(dayjs(start_date), "day") : true;
              },
            )
            .test("end_date_not_in_future", "La fecha de término no puede ser mayor al día actual", (value) =>
              value ? dayjs(value).isSameOrBefore(dayjs(), "day") : true,
            ),
        otherwise: (schema) => schema.notRequired(),
      }),
  });
};

const SI_NO = [
  { value: "true", label: "Sí" },
  { value: "false", label: "No" },
];

export const ExpSolicitudCompleta = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);

  const { isVisible } = useSolicitudCreditoForm("step_1");
  const { isVisible: isVisibleStep2, questions } = useSolicitudCreditoForm("step_2");
  const { isVisible: isVisibleStep3 } = useSolicitudCreditoForm("step_3");

  // ── APIs ──────────────────────────────────────────────────────────────────
  const [getCuentaById] = useLazyGetCuentasByIdQuery();
  const [patchCuenta] = usePatchCuentaMutation();
  const businessActivities = useGetBusinessActivitiesQuery();
  const [getByCp] = useLazyGetByCpQuery();
  const [getAddress] = useLazyGetAddressQuery();
  const [getAddressType] = useLazyGetAddressTypeQuery();
  const [getPersons] = useLazyGetPersonsQuery();
  const [postAddress] = usePostAddressMutation();
  const [patchAddress] = usePatchAddressMutation();

  const [getPaymentMethodById] = useLazyGetPaymentMethodByIdQuery();
  const [getPaymentMethodCatalog] = useLazyGetPaymentMethodQuery();
  const [postPaymentMethod] = usePostPaymentMethodMutation();
  const [putApplicationCompleted] = usePutApplicationCompletedMutation();
  const [postDynamicQuestion] = usePostDynamicQuestionMutation();
  const [getDynamicQuestion] = useLazyGetDynamicQuestionByApplicationQuery();

  const [getPaymentDetails] = useLazyGetPaymentDetailsQuery();
  const [postPaymentDetails] = usePostPaymentDetailsMutation();
  const [putPaymentDetails] = usePutPaymentDetailsMutation();
  const [getPepsType] = useLazyGetPepsTypeQuery();
  const [getPepsByPerson] = useLazyGetPepsByPersonQuery();
  const [getDependencias] = useLazyGetDependeciasQuery();
  const [getCargoPublico] = useLazyGetCargoPublicoQuery();
  const [getRelationship] = useLazyGetRelationshipQuery();
  const [postPeps] = usePotsPepsDataMutation();
  const [patchPepsData] = usePatchPepsDataMutation();
  const [deletePeps] = useDeletePepsMutation();

  // ── State ─────────────────────────────────────────────────────────────────
  const [isLoading, setIsLoading] = useState(true);
  const [openIdx, setOpenIdx] = useState<Set<number>>(new Set([0, 1, 2, 3, 4]));

  const [colonias, setColonias] = useState<{ id: number; name: string }[]>([]);
  const [coloniasParticular, setColoniasParticular] = useState<{ id: number; name: string }[]>([]);
  const [addressTypeIdOperativo, setAddressTypeIdOperativo] = useState<string | null>(null);
  const [addressTypeIdParticular, setAddressTypeIdParticular] = useState<string | null>(null);
  const [addressIdOperativo, setAddressIdOperativo] = useState<string | null>(null);
  const [addressIdParticular, setAddressIdParticular] = useState<string | null>(null);
  const [personIdRepresentante, setPersonIdRepresentante] = useState<string | null>(null);

  const [paymentMethodList, setPaymentMethodList] = useState<PaymentMethod[]>([]);
  const [idOtherPayment, setIdOtherPayment] = useState("");

  const [idDetallePeps, setIdDetallePeps] = useState<string | null>(null);
  const [pepsIdSocio, setPepsIdSocio] = useState("");
  const [pepsIdFamiliar, setPepsIdFamiliar] = useState("");
  const [listDependencias, setListDependencias] = useState<DependenciasData[]>([]);
  const [listCargos, setListCargos] = useState<CargoPublicoData[]>([]);
  const [listParentescos, setListParentescos] = useState<RelationshipData[]>([]);

  const hasTransaccionalContent = isVisibleStep2("paymentMethods") || questions.length > 0;
  const hasPepsContent = isVisibleStep3("socioFuncionario") || isVisibleStep3("conyuge");

  // ── Formik ────────────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues: {
      // step_1: empresa
      country_code: "+52",
      cell_phone_number: "",
      giro: "",
      descripcion: "",
      codigoPostal: "",
      calle: "",
      noInterior: "",
      noExterior: "",
      estadoId: "",
      ciudadId: "",
      localidadId: "",
      coloniaId: "",
      estado: "",
      ciudad: "",
      localidad: "",
      otraColonia: "",
      particular_codigoPostal: "",
      particular_calle: "",
      particular_noInterior: "",
      particular_noExterior: "",
      particular_estadoId: "",
      particular_ciudadId: "",
      particular_localidadId: "",
      particular_coloniaId: "",
      particular_estado: "",
      particular_ciudad: "",
      particular_localidad: "",
      particular_otraColonia: "",
      // step_2: transaccional
      paymentMethods: [] as string[],
      otherPaymentMethod: "",
      otro: false,
      dynamicAnswers: {} as Record<string, DynamicQuestionAnswerInput>,
      // step_3: peps
      socioFuncionario: "",
      conyuge: "",
      peps_socio: [emptyPep()],
      peps_conyuge: [emptyPep()],
    },
    validationSchema: yup.object().shape({
      // step_1
      country_code: yup.string().required("Campo requerido"),
      cell_phone_number: yup.string().required("Campo requerido").matches(PHONE_NUMBER, "Número de teléfono inválido"),
      giro: isVisible("giro") ? yup.string().required("Requerido") : yup.string().notRequired(),
      descripcion: isVisible("descripcion") ? yup.string().required("Requerido") : yup.string().notRequired(),
      codigoPostal: isVisible("domicilio_operativo")
        ? yup
            .string()
            .matches(/^[0-9]{5}$/, "Debe ser un código postal válido")
            .required("Requerido")
        : yup.string().notRequired(),
      calle: isVisible("domicilio_operativo") ? yup.string().required("Requerido") : yup.string().notRequired(),
      noExterior: isVisible("domicilio_operativo") ? yup.string().required("Requerido") : yup.string().notRequired(),
      noInterior: yup.string().notRequired(),
      estadoId: isVisible("domicilio_operativo") ? yup.string().required("Requerido") : yup.string().notRequired(),
      ciudadId: yup.string().notRequired(),
      coloniaId: isVisible("domicilio_operativo") ? yup.string().required("Requerido") : yup.string().notRequired(),
      localidadId: isVisible("domicilio_operativo") ? yup.string().required("Requerido") : yup.string().notRequired(),
      otraColonia: yup.string().when("coloniaId", {
        is: "-1",
        then: (schema) => (isVisible("domicilio_operativo") ? schema.required("Requerido") : schema.notRequired()),
        otherwise: (schema) => schema.notRequired(),
      }),
      particular_codigoPostal: isVisible("domicilio_particular")
        ? yup
            .string()
            .matches(/^[0-9]{5}$/, "Debe ser un código postal válido")
            .required("Requerido")
        : yup.string().notRequired(),
      particular_calle: isVisible("domicilio_particular")
        ? yup.string().required("Requerido")
        : yup.string().notRequired(),
      particular_noExterior: isVisible("domicilio_particular")
        ? yup.string().required("Requerido")
        : yup.string().notRequired(),
      particular_noInterior: yup.string().notRequired(),
      particular_estadoId: isVisible("domicilio_particular")
        ? yup.string().required("Requerido")
        : yup.string().notRequired(),
      particular_ciudadId: yup.string().notRequired(),
      particular_coloniaId: isVisible("domicilio_particular")
        ? yup.string().required("Requerido")
        : yup.string().notRequired(),
      particular_localidadId: isVisible("domicilio_particular")
        ? yup.string().required("Requerido")
        : yup.string().notRequired(),
      particular_otraColonia: yup.string().when("particular_coloniaId", {
        is: "-1",
        then: (schema) => (isVisible("domicilio_particular") ? schema.required("Requerido") : schema.notRequired()),
        otherwise: (schema) => schema.notRequired(),
      }),
      // step_2
      paymentMethods: hasTransaccionalContent
        ? yup.array().min(1, "Selecciona al menos una opción").of(yup.string().required()).required()
        : yup.array().notRequired(),
      otro: yup.boolean(),
      otherPaymentMethod: yup.string().when("otro", {
        is: true,
        then: (schema) => schema.required("Este campo es requerido cuando la opción otro esta seleccionada"),
        otherwise: (schema) => schema.notRequired(),
      }),
      dynamicAnswers: buildDynamicAnswersValidationSchema(questions),
      // step_3
      socioFuncionario: hasPepsContent ? yup.string().required("Campo requerido") : yup.string().notRequired(),
      conyuge: hasPepsContent ? yup.string().required("Campo requerido") : yup.string().notRequired(),
      peps_socio: hasPepsContent ? yup.array().of(pepItemSchema("socioFuncionario", false)) : yup.array().notRequired(),
      peps_conyuge: hasPepsContent ? yup.array().of(pepItemSchema("conyuge", true)) : yup.array().notRequired(),
    }),
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        // 1. Empresa: cuenta
        await patchCuenta({
          accountId: application.client_details_id,
          body: {
            country_code: values.country_code,
            cell_phone_number: values.cell_phone_number,
            ...(isVisible("giro") && { business_activity_id: values.giro || null }),
            ...(isVisible("descripcion") && { business_description: values.descripcion }),
          },
        }).unwrap();

        // 2. Domicilio operativo
        if (isVisible("domicilio_operativo") && addressTypeIdOperativo) {
          const body = {
            address_type_id: addressTypeIdOperativo,
            client_details_id: application.client_details_id,
            person_id: null,
            cp: values.codigoPostal,
            street: values.calle,
            no_ext: values.noExterior,
            no_int: values.noInterior,
            country_id: 1,
            state_id: values.estadoId ? +values.estadoId : null,
            city_id: values.ciudadId ? +values.ciudadId : null,
            colonia_id: String(values.coloniaId) === "-1" ? null : Number(values.coloniaId) || null,
            other_neighborhood: values.otraColonia || null,
          };
          if (addressIdOperativo) {
            await patchAddress({ addressId: addressIdOperativo, data: body }).unwrap();
          } else {
            const nuevo = await postAddress(body).unwrap();
            setAddressIdOperativo(nuevo.data?.id ?? null);
          }
        }

        // 3. Domicilio particular
        if (isVisible("domicilio_particular") && addressTypeIdParticular) {
          const body = {
            address_type_id: addressTypeIdParticular,
            client_details_id: application.client_details_id,
            person_id: personIdRepresentante,
            cp: values.particular_codigoPostal,
            street: values.particular_calle,
            no_ext: values.particular_noExterior,
            no_int: values.particular_noInterior,
            country_id: 1,
            state_id: values.particular_estadoId ? +values.particular_estadoId : null,
            city_id: values.particular_ciudadId ? +values.particular_ciudadId : null,
            colonia_id:
              String(values.particular_coloniaId) === "-1" ? null : Number(values.particular_coloniaId) || null,
            other_neighborhood: values.particular_otraColonia || null,
          };
          if (addressIdParticular) {
            await patchAddress({ addressId: addressIdParticular, data: body }).unwrap();
          } else {
            const nuevo = await postAddress(body).unwrap();
            setAddressIdParticular(nuevo.data?.id ?? null);
          }
        }

        // 4. Métodos de pago
        if (hasTransaccionalContent) {
          await postPaymentMethod({
            solicitudId: application.id,
            body: {
              payment_methods: values.paymentMethods.map((id) => ({
                payment_method_id: id,
                other_payment_method: id === idOtherPayment ? values.otherPaymentMethod : null,
              })),
            },
          }).unwrap();

          // 5. Preguntas dinámicas
          if (questions.length > 0) {
            const visibleQuestions = questions.filter((q) => isDynamicQuestionVisible(q, values.dynamicAnswers));
            const items = buildDynamicQuestionAnswerItems(visibleQuestions, values.dynamicAnswers);
            if (items.length > 0) {
              await postDynamicQuestion({ solicitudId: application.id, body: items }).unwrap();
            }
          }
        }

        // 6. PEPs: declaraciones y personas
        if (hasPepsContent) {
          const esSocio = values.socioFuncionario === "true";
          const esFamiliar = values.conyuge === "true";

          const pepDetailsBody = { pep_shareholder: esSocio, pep_members: esFamiliar };
          if (idDetallePeps) {
            await putPaymentDetails({
              solicitudId: application.id,
              detailId: idDetallePeps,
              body: pepDetailsBody,
            }).unwrap();
          } else {
            const nuevo = await postPaymentDetails({ solicitudId: application.id, body: pepDetailsBody }).unwrap();
            setIdDetallePeps(nuevo.data.id);
          }

          await syncPeps(values.peps_socio, esSocio, pepsIdSocio, false);
          await syncPeps(values.peps_conyuge, esFamiliar, pepsIdFamiliar, true);
        }

        // 7. Marcar solicitud como completa
        await putApplicationCompleted({
          id_sol: application.id,
          body: { loan_application_information_status: "COMPLETO" },
        }).unwrap();

        navigate(PATH_EXPEDIENTE);
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "ExpSolicitudCompleta-submit" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setSubmitting(false);
      }
    },
  });

  const { setFieldValue, setValues, validateForm } = formik;

  // ── Sync PEPs helper ──────────────────────────────────────────────────────
  const syncPeps = async (
    personas: PepPersonValue[],
    declarado: boolean,
    pepTypeId: string,
    conParentesco: boolean,
  ) => {
    if (!declarado) {
      for (const persona of personas) {
        if (persona.id) await deletePeps({ id: persona.id }).unwrap();
      }
      return;
    }
    for (const persona of personas) {
      const body: PayLoadPeps = {
        name: `${persona.nombre} ${persona.ap_paterno} ${persona.ap_materno}`,
        position: persona.cargo,
        dependency: persona.dependencia,
        active: persona.actualmente === "true",
        start_date: persona.start_date,
        end_date: persona.end_date === "" ? null : persona.end_date,
        loan_application_id: application.id,
        pep_type_id: pepTypeId,
        client_details_id: application.client_details_id,
        relationship_id: conParentesco ? persona.parentesco : null,
      };
      if (persona.id) {
        await patchPepsData({ id: persona.id, body }).unwrap();
      } else {
        await postPeps(body).unwrap();
      }
    }
  };

  // ── Fetch initial data (all sections) ─────────────────────────────────────
  const fetchInitialData = useCallback(async () => {
    try {
      setIsLoading(true);

      const detallesWrapped = async () => {
        try {
          return await getPaymentDetails({ solicitudId: application.id }).unwrap();
        } catch {
          return null;
        }
      };

      const [
        cuenta,
        resAddressType,
        resAddress,
        resPersons,
        metodosResponse,
        catalogoPagos,
        detallesResponse,
        tiposPeps,
        dependencias,
        cargos,
        parentescos,
      ] = await Promise.all([
        getCuentaById({ id: application.client_details_id }).unwrap(),
        getAddressType().unwrap(),
        getAddress({ accountId: application.client_details_id }).unwrap(),
        getPersons({
          loanApplicationId: application.id,
          personTypeCode: [PERSON_CATALOG.representante_legal.code],
        }).unwrap(),
        getPaymentMethodById({ solicitudId: application.id }).unwrap(),
        getPaymentMethodCatalog().unwrap(),
        detallesWrapped(),
        getPepsType().unwrap(),
        getDependencias().unwrap(),
        getCargoPublico().unwrap(),
        getRelationship().unwrap(),
      ]);

      // — Empresa —
      const addressType = resAddressType.find((item: Address) => item.code === ADDRESS_CODES.operativo.code);
      const address = resAddress.find((item: AddressData) => item.address_type_id === addressType?.id);
      const particularAddressType = resAddressType.find((item: Address) => item.code === ADDRESS_CODES.particular.code);
      const particularAddress = resAddress.find(
        (item: AddressData) => item.address_type_id === particularAddressType?.id,
      );

      setAddressTypeIdOperativo(addressType?.id ?? null);
      setAddressTypeIdParticular(particularAddressType?.id ?? null);
      setAddressIdOperativo(address?.id ?? null);
      setAddressIdParticular(particularAddress?.id ?? null);
      setPersonIdRepresentante(resPersons[0]?.id ?? null);

      // — Transaccional —
      const metodos = metodosResponse.data;
      const otroPayment = catalogoPagos.find((item) => item.code === PAYMENT_METHOD_CATALOG.otro.code);
      const metodoOtro = metodos?.find(
        (item: { payment_method?: { id: string } }) => item.payment_method?.id === otroPayment?.id,
      );
      setPaymentMethodList(catalogoPagos);
      setIdOtherPayment(otroPayment?.id || "");
      const metodoIds: string[] = (metodos
        ?.map((item: { payment_method?: { id: string } }) => item.payment_method?.id)
        .filter(Boolean) ?? []) as string[];

      // — PEPs —
      const detalles = detallesResponse?.data;
      const idSocio = String(tiposPeps.find((item) => item.name === PEP_TYPE_CATALOG.socio.name)?.id || "");
      const idFamiliar = String(tiposPeps.find((item) => item.name === PEP_TYPE_CATALOG.familiar.name)?.id || "");

      const [pepsPersona, pepsFamiliares] = await Promise.all([
        idSocio
          ? getPepsByPerson({ personId: application.client_details_id, pepsId: idSocio }).unwrap()
          : Promise.resolve([]),
        idFamiliar
          ? getPepsByPerson({ personId: application.client_details_id, pepsId: idFamiliar }).unwrap()
          : Promise.resolve([]),
      ]);

      const socios = (pepsPersona as PepsData[]).filter((item) => item.pep_type_id === idSocio).map(pepToValue);
      const familiares = (pepsFamiliares as PepsData[])
        .filter((item) => item.pep_type_id === idFamiliar)
        .map(pepToValue);

      setIdDetallePeps(detalles?.id ?? null);
      setPepsIdSocio(idSocio);
      setPepsIdFamiliar(idFamiliar);
      setListDependencias(dependencias);
      setListCargos(cargos);
      setListParentescos(parentescos);

      // — Valores del formulario —
      setValues((current) => ({
        ...current,
        // empresa
        country_code: cuenta.data.country_code || current.country_code,
        cell_phone_number: cuenta.data.cell_phone_number || "",
        giro: String(cuenta.data.business_activity_id || ""),
        descripcion: cuenta.data.business_description || "",
        ...(address
          ? {
              codigoPostal: address.cp || "",
              calle: address.street || "",
              noInterior: address.no_int || "",
              noExterior: address.no_ext || "",
              otraColonia: address.other_neighborhood || "",
            }
          : {}),
        ...(particularAddress
          ? {
              particular_codigoPostal: particularAddress.cp || "",
              particular_calle: particularAddress.street || "",
              particular_noInterior: particularAddress.no_int || "",
              particular_noExterior: particularAddress.no_ext || "",
              particular_otraColonia: particularAddress.other_neighborhood || "",
            }
          : {}),
        // transaccional
        paymentMethods: metodoIds,
        otherPaymentMethod: metodoOtro?.other_payment_method || "",
        otro: metodoIds.includes(otroPayment?.id || ""),
        // peps
        socioFuncionario: typeof detalles?.pep_shareholder === "boolean" ? String(detalles.pep_shareholder) : "",
        conyuge: typeof detalles?.pep_members === "boolean" ? String(detalles.pep_members) : "",
        peps_socio: socios.length ? socios : [emptyPep()],
        peps_conyuge: familiares.length ? familiares : [emptyPep()],
      }));

      if (address?.cp) fetchLocationData("operativo", address.cp, address.colonia_id || undefined);
      if (particularAddress?.cp)
        fetchLocationData("particular", particularAddress.cp, particularAddress.colonia_id || undefined);

      setTimeout(() => validateForm(), 200);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ExpSolicitudCompleta-initial_fetch" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
  }, [
    application.id,
    application.client_details_id,
    getCuentaById,
    getAddressType,
    getAddress,
    getPersons,
    getPaymentMethodById,
    getPaymentMethodCatalog,
    getPaymentDetails,
    getPepsType,
    getPepsByPerson,
    getDependencias,
    getCargoPublico,
    getRelationship,
    setValues,
    validateForm,
    dispatch,
  ]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Carga respuestas existentes de preguntas dinámicas.
  useEffect(() => {
    if (questions.length === 0) return;
    (async () => {
      try {
        const answers = await getDynamicQuestion(application.id).unwrap();
        setFieldValue("dynamicAnswers", mapDynamicQuestionAnswerRecordsToFormValues(answers, questions));
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "ExpSolicitudCompleta-fetchDynamicAnswers" } });
      }
    })();
  }, [questions.length]);

  // Limpia respuestas de preguntas condicionales cuando dejan de ser visibles.
  useEffect(() => {
    questions.forEach((question) => {
      if (!question.visible_when) return;
      const currentValue = formik.values.dynamicAnswers[question.code];
      const hasValue = Array.isArray(currentValue) ? currentValue.length > 0 : Boolean(currentValue);
      if (!hasValue) return;
      if (!isDynamicQuestionVisible(question, formik.values.dynamicAnswers)) {
        setFieldValue(`dynamicAnswers.${question.code}`, question.type === "multiple_choice" ? [] : "");
      }
    });
  }, [formik.values.dynamicAnswers, questions]);

  // Limpia otraColonia cuando cambia la colonia seleccionada.
  useEffect(() => {
    if (String(formik.values.coloniaId) !== "-1") setFieldValue("otraColonia", "");
  }, [formik.values.coloniaId, setFieldValue]);

  useEffect(() => {
    if (String(formik.values.particular_coloniaId) !== "-1") setFieldValue("particular_otraColonia", "");
  }, [formik.values.particular_coloniaId, setFieldValue]);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const toggle = (index: number) =>
    setOpenIdx((current) => {
      const next = new Set(current);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });

  const fetchLocationData = async (variant: "operativo" | "particular", codigoPostal: string, coloniaId?: number) => {
    const prefix = variant === "particular" ? "particular_" : "";
    const setColoniasVariant = variant === "particular" ? setColoniasParticular : setColonias;
    try {
      if (codigoPostal.length === 5) {
        const value = await getByCp(codigoPostal).unwrap();
        const { colonias: cols, cities, states, localities } = value;
        setColoniasVariant(cols);
        setFieldValue(`${prefix}estadoId`, states[0]?.id || "");
        setFieldValue(`${prefix}estado`, states[0]?.name || "");
        setFieldValue(`${prefix}ciudadId`, cities[0]?.id || "");
        setFieldValue(`${prefix}ciudad`, cities[0]?.name || "");
        setFieldValue(`${prefix}localidadId`, localities[0]?.id || "");
        setFieldValue(`${prefix}localidad`, localities[0]?.name || "");
        if (coloniaId) {
          const coloniaSelected = cols.find((item: ColoniaStructure) => item.id == coloniaId);
          setFieldValue(`${prefix}coloniaId`, coloniaSelected?.id || "");
        } else {
          setFieldValue(`${prefix}coloniaId`, cols[0]?.id || "");
        }
        setTimeout(() => validateForm(), 200);
      } else {
        setFieldValue(`${prefix}estadoId`, "");
        setFieldValue(`${prefix}estado`, "");
        setFieldValue(`${prefix}ciudadId`, "");
        setFieldValue(`${prefix}ciudad`, "");
        setFieldValue(`${prefix}localidadId`, "");
        setFieldValue(`${prefix}localidad`, "");
        setFieldValue(`${prefix}coloniaId`, "");
      }
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ExpSolicitudCompleta-fetchLocationData" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    }
  };

  const togglePaymentMethod = (paymentMethodId: string, checked: boolean) => {
    const seleccionados = checked
      ? [...formik.values.paymentMethods, paymentMethodId]
      : formik.values.paymentMethods.filter((id) => id !== paymentMethodId);
    setFieldValue("paymentMethods", seleccionados);
    setFieldValue("otro", seleccionados.includes(idOtherPayment));
    setTimeout(() => validateForm(), 200);
  };

  const responderDeclaracion = (
    campo: "socioFuncionario" | "conyuge",
    lista: "peps_socio" | "peps_conyuge",
    valor: string,
  ) => {
    setFieldValue(campo, valor);
    if (valor === "true" && formik.values[lista].length === 0) setFieldValue(lista, [emptyPep()]);
    setTimeout(() => validateForm(), 200);
  };

  const agregarPersona = (lista: "peps_socio" | "peps_conyuge") => {
    if (formik.values[lista].length >= MAX_PEPS) return;
    setFieldValue(lista, [...formik.values[lista], emptyPep()]);
  };

  const removerPersona = async (
    lista: "peps_socio" | "peps_conyuge",
    campo: "socioFuncionario" | "conyuge",
    index: number,
  ) => {
    try {
      const persona = formik.values[lista][index];
      if (persona.id) await deletePeps({ id: persona.id }).unwrap();
      const restantes = formik.values[lista].filter((_, i) => i !== index);
      setFieldValue(lista, restantes);
      if (restantes.length === 0) setFieldValue(campo, "false");
      setTimeout(() => validateForm(), 200);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "ExpSolicitudCompleta-removerPersona" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    }
  };

  const renderListaPeps = (
    lista: "peps_socio" | "peps_conyuge",
    campo: "socioFuncionario" | "conyuge",
    testIdBase: string,
    parentescos?: RelationshipData[],
  ) => (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
      {formik.values[lista].map((persona, index) => (
        <PepPersonFields
          key={persona.id || index}
          namePrefix={`${lista}[${index}]`}
          value={persona}
          touched={formik.touched[lista]?.[index]}
          errors={(formik.errors[lista] as FormikErrors<PepPersonValue>[] | undefined)?.[index]}
          dependencias={listDependencias}
          cargos={listCargos}
          parentescos={parentescos}
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          setFieldValue={setFieldValue}
          onRemove={() => removerPersona(lista, campo, index)}
          disabled={disabled}
          testIdPrefix={`${testIdBase}-${index}`}
        />
      ))}
      {formik.values[lista].length < MAX_PEPS && (
        <Typography
          component="button"
          type="button"
          variant="body2"
          onClick={() => agregarPersona(lista)}
          disabled={disabled}
          data-testid={`${testIdBase}-add-button`}
          sx={{
            alignSelf: "flex-start",
            background: "none",
            border: "none",
            p: 0,
            cursor: "pointer",
            color: "primary.main",
            fontWeight: 500,
            "&:disabled": { cursor: "not-allowed", opacity: 0.5 },
          }}
        >
          + Agregar otro
        </Typography>
      )}
    </Box>
  );

  // ── Índices dinámicos de acordeón ─────────────────────────────────────────
  const idxContacto = 1;
  const idxDomicilioOp = isVisible("domicilio_operativo") ? 2 : null;
  const idxDomicilioPart = isVisible("domicilio_particular") ? (idxDomicilioOp ? 3 : 2) : null;
  const baseTransaccional = [idxDomicilioOp, idxDomicilioPart].filter(Boolean).length + 2;
  const idxPeps = hasTransaccionalContent ? baseTransaccional + 1 : baseTransaccional;

  const disabled = isLoading || formik.isSubmitting;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box>
      <PageHeader
        eyebrow="Expediente digital · Empresa · Solicitud de crédito"
        title="Solicitud de crédito"
        subtitle="Revisa y completa toda la información antes de continuar con el expediente."
      />

      {isLoading ? (
        <Skeleton
          variant="rectangular"
          animation="wave"
          sx={{ height: ALTO_SKELETON_SOLICITUD_COMPLETA, borderRadius: "12px" }}
        />
      ) : (
        <Fragment>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {/* ── Sección 1: Datos de contacto de la empresa ─────────────── */}
            <AccordionCard
              index={idxContacto}
              title="Datos de contacto de la empresa"
              open={openIdx.has(0)}
              onToggle={() => toggle(0)}
            >
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                <FieldWrapper
                  label="Teléfono de la empresa"
                  full
                  error={
                    (formik.touched.country_code && formik.errors.country_code) ||
                    (formik.touched.cell_phone_number && formik.errors.cell_phone_number) ||
                    ""
                  }
                >
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <CountrySelect
                      value={formik.values.country_code}
                      onChange={(value) => setFieldValue("country_code", value)}
                      disabled={disabled}
                    />
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      fullWidth
                      name="cell_phone_number"
                      value={formik.values.cell_phone_number}
                      onChange={(e) => setFieldValue("cell_phone_number", e.target.value.replace(/[^0-9]/g, ""))}
                      onBlur={formik.handleBlur}
                      placeholder="55 1234 5678"
                      disabled={disabled}
                      inputProps={{
                        maxLength: 10,
                        inputMode: "numeric",
                        "data-testid": "expediente-solicitud-telefono-input",
                      }}
                    />
                  </Box>
                </FieldWrapper>

                {isVisible("giro") && (
                  <FieldWrapper
                    label="Giro"
                    full
                    loading={businessActivities.isLoading}
                    error={(formik.touched.giro && formik.errors.giro) || undefined}
                  >
                    <Select
                      size="lendia_input"
                      name="giro"
                      fullWidth
                      displayEmpty
                      value={formik.values.giro}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.giro && Boolean(formik.errors.giro)}
                      disabled={businessActivities.isLoading || disabled}
                      data-testid="expediente-solicitud-giro-select"
                      {...selectLoadingProps(businessActivities.isLoading)}
                    >
                      <MenuItem value="">Selecciona</MenuItem>
                      {businessActivities.currentData
                        ?.slice()
                        .sort((a, b) => a.description.localeCompare(b.description))
                        .map((a) => (
                          <MenuItem key={a.id} value={a.id}>
                            {a.description}
                          </MenuItem>
                        ))}
                    </Select>
                  </FieldWrapper>
                )}

                {isVisible("descripcion") && (
                  <FieldWrapper
                    label="Describe brevemente a qué se dedica tu empresa"
                    full
                    error={(formik.touched.descripcion && formik.errors.descripcion) || ""}
                  >
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      fullWidth
                      name="descripcion"
                      value={formik.values.descripcion}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      multiline
                      rows={3}
                      disabled={disabled}
                      InputProps={{
                        sx: {
                          height: "auto",
                          alignItems: "flex-start",
                          "& .MuiOutlinedInput-input": { height: "auto", padding: "10px 14px" },
                        },
                      }}
                      inputProps={{ maxLength: 500, "data-testid": "expediente-solicitud-descripcion-input" }}
                    />
                  </FieldWrapper>
                )}
              </Box>
            </AccordionCard>

            {/* ── Sección 2: Domicilio operativo ─────────────────────────── */}
            {isVisible("domicilio_operativo") && (
              <AccordionCard
                index={idxDomicilioOp ?? 2}
                title="Domicilio operativo"
                open={openIdx.has(1)}
                onToggle={() => toggle(1)}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <FieldWrapper
                    label="Código Postal"
                    full
                    error={(formik.touched.codigoPostal && formik.errors.codigoPostal) || ""}
                  >
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      fullWidth
                      name="codigoPostal"
                      value={formik.values.codigoPostal}
                      onChange={(e) => {
                        const cp = e.target.value.replace(/[^0-9]/g, "");
                        setFieldValue("codigoPostal", cp);
                        if (cp.length === 5) fetchLocationData("operativo", cp);
                      }}
                      onBlur={formik.handleBlur}
                      disabled={disabled}
                      inputProps={{
                        maxLength: 5,
                        inputMode: "numeric",
                        "data-testid": "expediente-solicitud-cp-input",
                      }}
                    />
                  </FieldWrapper>
                  <FieldWrapper
                    label="Calle o avenida"
                    full
                    error={(formik.touched.calle && formik.errors.calle) || ""}
                  >
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      fullWidth
                      name="calle"
                      value={formik.values.calle}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={disabled}
                      inputProps={{ maxLength: 100, "data-testid": "expediente-solicitud-calle-input" }}
                    />
                  </FieldWrapper>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FieldWrapper
                        label="No. Exterior"
                        full
                        error={(formik.touched.noExterior && formik.errors.noExterior) || ""}
                      >
                        <TextField
                          autoComplete="off"
                          size="lendia_input"
                          fullWidth
                          name="noExterior"
                          value={formik.values.noExterior}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={disabled}
                          inputProps={{ maxLength: 50, "data-testid": "expediente-solicitud-noext-input" }}
                        />
                      </FieldWrapper>
                    </Grid>
                    <Grid item xs={6}>
                      <FieldWrapper
                        label="No. Interior"
                        full
                        error={(formik.touched.noInterior && formik.errors.noInterior) || ""}
                      >
                        <TextField
                          autoComplete="off"
                          size="lendia_input"
                          fullWidth
                          name="noInterior"
                          value={formik.values.noInterior}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={disabled}
                          inputProps={{ maxLength: 50, "data-testid": "expediente-solicitud-noint-input" }}
                        />
                      </FieldWrapper>
                    </Grid>
                  </Grid>
                  <FieldWrapper
                    label="Colonia"
                    full
                    error={(formik.touched.coloniaId && formik.errors.coloniaId) || undefined}
                  >
                    <Select
                      fullWidth
                      size="lendia_input"
                      displayEmpty
                      name="coloniaId"
                      value={formik.values.coloniaId}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.coloniaId && Boolean(formik.errors.coloniaId)}
                      disabled={disabled}
                      data-testid="expediente-solicitud-colonia-select"
                    >
                      <MenuItem value="">Selecciona</MenuItem>
                      {colonias.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FieldWrapper>
                  {String(formik.values.coloniaId) === "-1" && (
                    <FieldWrapper
                      label="Otra colonia"
                      full
                      error={(formik.touched.otraColonia && formik.errors.otraColonia) || ""}
                    >
                      <TextField
                        autoComplete="off"
                        size="lendia_input"
                        fullWidth
                        name="otraColonia"
                        value={formik.values.otraColonia}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={disabled}
                        inputProps={{ "data-testid": "expediente-solicitud-otra-colonia-input" }}
                      />
                    </FieldWrapper>
                  )}
                  <FieldWrapper label="Ciudad" full error={(formik.touched.ciudadId && formik.errors.ciudadId) || ""}>
                    <TextField size="lendia_input" fullWidth name="ciudad" value={formik.values.ciudad} disabled />
                  </FieldWrapper>
                  <FieldWrapper
                    label="Municipio"
                    full
                    error={(formik.touched.localidadId && formik.errors.localidadId) || ""}
                  >
                    <TextField
                      size="lendia_input"
                      fullWidth
                      name="localidad"
                      value={formik.values.localidad}
                      disabled
                    />
                  </FieldWrapper>
                  <FieldWrapper
                    label="Entidad federativa"
                    full
                    error={(formik.touched.estadoId && formik.errors.estadoId) || ""}
                  >
                    <TextField size="lendia_input" fullWidth name="estado" value={formik.values.estado} disabled />
                  </FieldWrapper>
                </Box>
              </AccordionCard>
            )}

            {/* ── Sección 3: Domicilio particular ────────────────────────── */}
            {isVisible("domicilio_particular") && (
              <AccordionCard
                index={idxDomicilioPart ?? 2}
                title="Domicilio particular"
                open={openIdx.has(2)}
                onToggle={() => toggle(2)}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <FieldWrapper
                    label="Código Postal"
                    full
                    error={(formik.touched.particular_codigoPostal && formik.errors.particular_codigoPostal) || ""}
                  >
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      fullWidth
                      name="particular_codigoPostal"
                      value={formik.values.particular_codigoPostal}
                      onChange={(e) => {
                        const cp = e.target.value.replace(/[^0-9]/g, "");
                        setFieldValue("particular_codigoPostal", cp);
                        if (cp.length === 5) fetchLocationData("particular", cp);
                      }}
                      onBlur={formik.handleBlur}
                      disabled={disabled}
                      inputProps={{
                        maxLength: 5,
                        inputMode: "numeric",
                        "data-testid": "expediente-solicitud-particular-cp-input",
                      }}
                    />
                  </FieldWrapper>
                  <FieldWrapper
                    label="Calle o avenida"
                    full
                    error={(formik.touched.particular_calle && formik.errors.particular_calle) || ""}
                  >
                    <TextField
                      autoComplete="off"
                      size="lendia_input"
                      fullWidth
                      name="particular_calle"
                      value={formik.values.particular_calle}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      disabled={disabled}
                      inputProps={{ maxLength: 100, "data-testid": "expediente-solicitud-particular-calle-input" }}
                    />
                  </FieldWrapper>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <FieldWrapper
                        label="No. Exterior"
                        full
                        error={(formik.touched.particular_noExterior && formik.errors.particular_noExterior) || ""}
                      >
                        <TextField
                          autoComplete="off"
                          size="lendia_input"
                          fullWidth
                          name="particular_noExterior"
                          value={formik.values.particular_noExterior}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={disabled}
                          inputProps={{ maxLength: 50, "data-testid": "expediente-solicitud-particular-noext-input" }}
                        />
                      </FieldWrapper>
                    </Grid>
                    <Grid item xs={6}>
                      <FieldWrapper
                        label="No. Interior"
                        full
                        error={(formik.touched.particular_noInterior && formik.errors.particular_noInterior) || ""}
                      >
                        <TextField
                          autoComplete="off"
                          size="lendia_input"
                          fullWidth
                          name="particular_noInterior"
                          value={formik.values.particular_noInterior}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          disabled={disabled}
                          inputProps={{ maxLength: 50, "data-testid": "expediente-solicitud-particular-noint-input" }}
                        />
                      </FieldWrapper>
                    </Grid>
                  </Grid>
                  <FieldWrapper
                    label="Colonia"
                    full
                    error={(formik.touched.particular_coloniaId && formik.errors.particular_coloniaId) || undefined}
                  >
                    <Select
                      fullWidth
                      size="lendia_input"
                      displayEmpty
                      name="particular_coloniaId"
                      value={formik.values.particular_coloniaId}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={formik.touched.particular_coloniaId && Boolean(formik.errors.particular_coloniaId)}
                      disabled={disabled}
                      data-testid="expediente-solicitud-particular-colonia-select"
                    >
                      <MenuItem value="">Selecciona</MenuItem>
                      {coloniasParticular.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FieldWrapper>
                  {String(formik.values.particular_coloniaId) === "-1" && (
                    <FieldWrapper
                      label="Otra colonia"
                      full
                      error={(formik.touched.particular_otraColonia && formik.errors.particular_otraColonia) || ""}
                    >
                      <TextField
                        autoComplete="off"
                        size="lendia_input"
                        fullWidth
                        name="particular_otraColonia"
                        value={formik.values.particular_otraColonia}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={disabled}
                        inputProps={{ "data-testid": "expediente-solicitud-particular-otra-colonia-input" }}
                      />
                    </FieldWrapper>
                  )}
                  <FieldWrapper
                    label="Ciudad"
                    full
                    error={(formik.touched.particular_ciudadId && formik.errors.particular_ciudadId) || ""}
                  >
                    <TextField
                      size="lendia_input"
                      fullWidth
                      name="particular_ciudad"
                      value={formik.values.particular_ciudad}
                      disabled
                    />
                  </FieldWrapper>
                  <FieldWrapper
                    label="Municipio"
                    full
                    error={(formik.touched.particular_localidadId && formik.errors.particular_localidadId) || ""}
                  >
                    <TextField
                      size="lendia_input"
                      fullWidth
                      name="particular_localidad"
                      value={formik.values.particular_localidad}
                      disabled
                    />
                  </FieldWrapper>
                  <FieldWrapper
                    label="Entidad federativa"
                    full
                    error={(formik.touched.particular_estadoId && formik.errors.particular_estadoId) || ""}
                  >
                    <TextField
                      size="lendia_input"
                      fullWidth
                      name="particular_estado"
                      value={formik.values.particular_estado}
                      disabled
                    />
                  </FieldWrapper>
                </Box>
              </AccordionCard>
            )}

            {/* ── Sección 4: Información transaccional ───────────────────── */}
            {hasTransaccionalContent && (
              <AccordionCard
                index={baseTransaccional}
                title="Información transaccional"
                open={openIdx.has(3)}
                onToggle={() => toggle(3)}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  {isVisibleStep2("paymentMethods") && (
                    <FieldWrapper
                      label="¿De qué forma realizarás tus pagos?"
                      full
                      error={(formik.touched.paymentMethods && (formik.errors.paymentMethods as string)) || ""}
                    >
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                        {[...paymentMethodList]
                          .sort((a, b) =>
                            a.code === PAYMENT_METHOD_CATALOG.otro.code
                              ? 1
                              : b.code === PAYMENT_METHOD_CATALOG.otro.code
                                ? -1
                                : 0,
                          )
                          .map((pm) => (
                            <Checkbox
                              key={pm.id}
                              checked={formik.values.paymentMethods.includes(pm.id)}
                              onChange={(checked) => togglePaymentMethod(pm.id, checked)}
                              disabled={disabled}
                              label={
                                <Typography
                                  variant="body2"
                                  color="primary.dark"
                                  data-testid={`expediente-transaccional-metodo-pago-${pm.code}`}
                                >
                                  {pm.description}
                                </Typography>
                              }
                            />
                          ))}
                      </Box>
                    </FieldWrapper>
                  )}

                  {formik.values.otro && (
                    <FieldWrapper
                      label="Especifica el método de pago"
                      full
                      error={(formik.touched.otherPaymentMethod && formik.errors.otherPaymentMethod) || ""}
                    >
                      <TextField
                        autoComplete="off"
                        size="lendia_input"
                        fullWidth
                        name="otherPaymentMethod"
                        value={formik.values.otherPaymentMethod}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        disabled={disabled}
                        inputProps={{ maxLength: 100, "data-testid": "expediente-transaccional-otro-metodo-input" }}
                      />
                    </FieldWrapper>
                  )}

                  {questions
                    .filter((q) => isDynamicQuestionVisible(q, formik.values.dynamicAnswers))
                    .map((question) => {
                      const touchedAnswer = formik.touched.dynamicAnswers?.[question.code];
                      const answerError = (formik.errors.dynamicAnswers as Record<string, string> | undefined)?.[
                        question.code
                      ];
                      return (
                        <FieldWrapper key={question.code} label="" full error={(touchedAnswer && answerError) || ""}>
                          <CustomDynamicQuestionField
                            variant="v2"
                            question={question}
                            value={
                              formik.values.dynamicAnswers[question.code] ??
                              (question.type === "multiple_choice" ? [] : "")
                            }
                            onChange={(v) => setFieldValue(`dynamicAnswers.${question.code}`, v)}
                            onBlur={formik.handleBlur}
                            disabled={disabled}
                            error={Boolean(touchedAnswer && answerError)}
                            helperText={touchedAnswer && answerError}
                          />
                        </FieldWrapper>
                      );
                    })}
                </Box>
              </AccordionCard>
            )}

            {/* ── Sección 5: Declaraciones regulatorias (PEP) ────────────── */}
            {hasPepsContent && (
              <AccordionCard
                index={idxPeps}
                title="Declaraciones regulatorias (PEP)"
                open={openIdx.has(4)}
                onToggle={() => toggle(4)}
              >
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <FieldWrapper
                    label="¿El representante legal, propietarios reales o accionistas desempeña o ha desempeñado funciones públicas destacadas en México o en el extranjero, altos puestos ejecutivos en empresas estatales o funciones importantes en partidos políticos, en el último año?"
                    full
                    error={(formik.touched.socioFuncionario && formik.errors.socioFuncionario) || ""}
                  >
                    <ChoiceToggle
                      value={formik.values.socioFuncionario}
                      onChange={(v) => responderDeclaracion("socioFuncionario", "peps_socio", String(v))}
                      options={SI_NO}
                      disabled={disabled}
                      size="small"
                    />
                  </FieldWrapper>

                  {formik.values.socioFuncionario === "true" &&
                    renderListaPeps("peps_socio", "socioFuncionario", "expediente-declaraciones-pep-socio")}

                  <FieldWrapper
                    label="¿El cónyuge o alguno de los padres, abuelos, hijos, nietos, hermanos, suegros, hijos políticos, abuelos políticos o cuñados de los socios, accionistas o propietarios reales, desempeña o ha desempeñado funciones públicas destacadas en México o en el extranjero, altos puestos ejecutivos en empresas estatales o funciones importantes en partidos políticos?"
                    full
                    error={(formik.touched.conyuge && formik.errors.conyuge) || ""}
                  >
                    <ChoiceToggle
                      value={formik.values.conyuge}
                      onChange={(v) => responderDeclaracion("conyuge", "peps_conyuge", String(v))}
                      options={SI_NO}
                      disabled={disabled}
                      size="small"
                    />
                  </FieldWrapper>

                  {formik.values.conyuge === "true" &&
                    renderListaPeps(
                      "peps_conyuge",
                      "conyuge",
                      "expediente-declaraciones-pep-familiar",
                      listParentescos,
                    )}

                  <Callout tone="warn">
                    Las personas políticamente expuestas (PEP) son individuos que ocupan o han ocupado cargos públicos
                    relevantes. Esta información es requerida por regulación.
                  </Callout>
                </Box>
              </AccordionCard>
            )}
          </Box>

          <StepActions
            onBack={() => navigate(PATH_EXPEDIENTE)}
            onNext={formik.submitForm}
            backLabel="Regresar al Expediente"
            nextLabel="Guardar y continuar al Expediente"
            nextDisabled={disabled || !formik.isValid}
            backTestId="expediente-completa-back-button"
            nextTestId="expediente-completa-continue-button"
          />
        </Fragment>
      )}
    </Box>
  );
};
